import React, { useState, useRef, useEffect } from 'react';
import { Theme, Grid, Button } from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import {
	OrderObject,
	PaymentRequest,
	PaymentMethod,
	TransactionRequest,
	ReceiptApi,
	OrderPaymentRequest,
} from 'common/types';
import * as ShopSelector from 'selectors/ShopSelectors';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import CloseIcon from '@mui/icons-material/Close';
import DialogContent from '@mui/material/DialogContent';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import PaymentRow from '../SideBar/PaymentSection/PaymentRow';
import Spinner from 'common/components/Spinner';
import { getPricingString } from 'common/utils/common';
import classNames from 'classnames';
import Container from 'common/components/Container';
import { newFirestoreId } from 'common/utils/newRentalUtils';
import { createPaymentObjects, getItemsForPayment } from 'common/utils/payments';
import { getShopReceiptInfo } from 'common/utils/shopUtils';
import ReactToPrint from 'react-to-print';
import PaymentReceipt from 'components/PaymentReceipt';
import { RiUploadLine } from 'react-icons/ri';
import { getDefaultPaymentOptions } from 'services/utils';
import CustomPaymentMethodsDialog from 'components/CustomPaymentMethodsDialog';
import { getCurrencyObjectFromCurrencyCode } from 'common/utils/currencyUtils';
import { useSelector } from 'react-redux';
import { useTranslation } from 'services/localization';
import { LocaleHelpers } from 'common/locales/helpers';
import {
	getItemsPayments,
	getOrderPayments,
	getOrderDeliveryPayments,
	getOrderDeliveryForPayment,
} from 'common/modules/atoms/paymentAmounts';
import { runTransaction, FieldValues } from 'common/frontend/firebase/firestore';
import { api } from 'common/frontend/api';

interface OwnProps {
	open: boolean;
	order: OrderObject;
	handleClose: () => void;
}

type Props = OwnProps & WithStyles<typeof styles>;

let timeout: any = null;

const PayDialog = (props: Props) => {
	const { classes, order, open, handleClose } = props;
	const { rentalInfo, products, orderDelivery } = order;
	const shop = useSelector(ShopSelector.activeShopData)!;
	const shopCurrency = useSelector(ShopSelector.getShopCurrency);
	const { t } = useTranslation();
	const currency = getCurrencyObjectFromCurrencyCode(rentalInfo.pricing.currency, shopCurrency);
	const receiptRef = useRef<HTMLDivElement | null>(null);
	const chargePayment = getOrderPayments(rentalInfo).charge;
	const productsForPayment = products.filter((p) => {
		const productChargePayment = getItemsPayments(p).charge;
		return productChargePayment.unpaid > 0 || productChargePayment.total === 0;
	});

	const orderDeliveryForPayment = getOrderDeliveryForPayment(orderDelivery);

	const unpaidAmount = chargePayment.unpaid;
	const [amountToPay] = useState<number>(unpaidAmount);
	const [inputAmount, setInputAmount] = useState(amountToPay);
	const [isInitialAmount, setIsInitialAmount] = useState<boolean>(true);
	const [paymentLoading, setPaymentLoading] = useState(false);
	const [paymentError, setPaymentError] = useState('');
	const [transactionsInfo, setTransactionsInfo] = useState<TransactionRequest[]>([]);
	const [changeToPay, setChangeToPay] = useState<number>(0);
	const pendingPaid = transactionsInfo
		.map((t) => t.amount.value)
		.reduce((tot, curr) => tot + curr, 0);
	const leftToPay = amountToPay - pendingPaid;
	const [receipt, setReceipt] = useState<ReceiptApi | null>(null);
	const [isPrinting, setIsPrinting] = useState<boolean>(false);
	const [customPaymentMethodsDialogOpen, setCustomPaymentMethodsDialogOpen] = useState<boolean>(
		false,
	);

	useEffect(() => {
		if (isPrinting && receipt) {
			const printButton = document.getElementById('print-pay-receipt-button');
			if (printButton) {
				printButton.click();
			}
		}
	}, [isPrinting, receipt]);

	const resetAmountTo = (amount: number) => {
		setInputAmount(amount);
		setIsInitialAmount(true);
	};

	const setNewInputAmount = (addedValueString: string) => {
		const newInputAmount = Number((isInitialAmount ? 0 : inputAmount) + addedValueString);
		setInputAmount(newInputAmount);
		if (newInputAmount > leftToPay) {
			if (timeout) {
				clearTimeout(timeout);
			}
			timeout = setTimeout(() => {
				timeout = null;
				setInputAmount(leftToPay);
				setIsInitialAmount(true);
			}, 1000);
		}
		if (isInitialAmount) {
			setIsInitialAmount(false);
		}
	};

	const handlePrintingDone = () => {
		if (changeToPay <= 0) {
			handleClose();
		}
		setIsPrinting(false);
	};

	const payAmount = async (method: PaymentMethod) => {
		let amountBeingPaid = Math.min(inputAmount, leftToPay);
		if (amountBeingPaid === 0) {
			return;
		}
		let changeToPay = 0;
		const amountStillToPay = amountToPay - pendingPaid - amountBeingPaid;
		if (amountStillToPay < 0) {
			changeToPay = Math.abs(amountStillToPay);
			setChangeToPay(changeToPay);
			amountBeingPaid = amountToPay - pendingPaid;
		}
		const transactionRequest: TransactionRequest = {
			transactionId: newFirestoreId(),
			method,
			pspReference: null,
			provider: 'MANUAL',
			amount: {
				value: amountBeingPaid,
				currency: rentalInfo.pricing.currency,
			},
			splitData: null,
		};
		const newTransactionsInfo = [...transactionsInfo, transactionRequest];
		setTransactionsInfo([...transactionsInfo, transactionRequest]);
		setInputAmount(amountStillToPay);
		setIsInitialAmount(true);
		if (amountStillToPay <= 0) {
			setPaymentLoading(true);
			try {
				const receipt = await handleOrderAndPaymentSaveToDb(order, newTransactionsInfo);
				setReceipt(receipt);
				setIsPrinting(true);
			} catch (e) {
				setPaymentError(e.message || 'Payment error');
			}
			setPaymentLoading(false);
		}
	};

	const handleOrderAndPaymentSaveToDb = async (
		order: OrderObject,
		transactionsInfo: TransactionRequest[],
	) => {
		const { shopId, startDate, live, startLocation } = rentalInfo;
		const env = live ? 'live' : 'test';
		const translation = t;
		const paymentAmount = transactionsInfo
			.map((t) => t.amount.value)
			.reduce((tot, curr) => tot + curr, 0);
		try {
			const receipt = await runTransaction<ReceiptApi>(async (t) => {
				const transactionApi = api(t);
				let receiptNumber: number = 1000;
				receiptNumber =
					shop.shopInfo.receiptNumber && shop.shopInfo.receiptNumber[env]
						? shop.shopInfo.receiptNumber[env] + 1
						: receiptNumber;
				const paymentNumber = (shop.shopInfo.paymentNumber || 0) + 1;
				const orderPaymentRequestId = newFirestoreId();
				transactionApi.shops.doc(shopId).update({
					[`receiptNumber.${env}` as `receiptNumber.test`]: receiptNumber,
					paymentNumber,
				});
				const amount = {
					value: paymentAmount,
					currency: rentalInfo.pricing.currency,
				};
				const paymentRequest: PaymentRequest = {
					items: getItemsForPayment(
						productsForPayment,
						orderDeliveryForPayment,
						amount,
						translation,
					),
					productIds: productsForPayment.map((p) => p.id),
					orderId: rentalInfo.id,
					shopId,
					amount,
					orderPaymentRequestId,
					shopperId:
						(!rentalInfo.responsiblePerson.external && rentalInfo.responsiblePerson.shopperId) ||
						null,
					live,
					locationId: startLocation.id,
					channel: 'ADMIN',
					startDate,
					receiptNumber,
					shopReceiptInfo: getShopReceiptInfo(shop.publicInfo, startLocation.id),
					transactionsInfo,
					paymentType: 'CHARGE',
					capture: true,
					paymentAction: 'PAYMENT',
				};
				const { payment, transactions, receipt } = createPaymentObjects(paymentRequest);
				transactionApi.payments.doc(payment.id).set(payment);
				for (const transaction of transactions) {
					transactionApi.transactions.doc(transaction.id).set(transaction);
				}
				transactionApi.receipts.doc(receipt.id).set(receipt);
				transactionApi.orders.doc(rentalInfo.id).update({
					'charge.paid': (order.rentalInfo.charge.paid || 0) + paymentAmount,
					'charge.captured': (order.rentalInfo.charge.captured || 0) + paymentAmount,
					'charge.authorised': (order.rentalInfo.charge.authorised || 0) + paymentAmount,
				});
				for (const product of productsForPayment) {
					const productPaid = getItemsPayments(product).charge.unpaid;
					transactionApi.orderProducts.doc(product.id).update({
						'charge.paid': FieldValues.increment(productPaid),
						'charge.captured': FieldValues.increment(productPaid),
						'charge.authorised': FieldValues.increment(productPaid),
					});
				}
				if (!!orderDeliveryForPayment) {
					const servicePaid = getOrderDeliveryPayments(orderDeliveryForPayment).charge.unpaid;
					transactionApi.orderDeliveries.doc(orderDeliveryForPayment.id).update({
						'charge.paid': FieldValues.increment(servicePaid),
						'charge.captured': FieldValues.increment(servicePaid),
						'charge.authorised': FieldValues.increment(servicePaid),
					});
				}
				const orderPaymentRequest: OrderPaymentRequest = {
					id: orderPaymentRequestId,
					paymentNumber,
					shopId,
					orderId: rentalInfo.id,
					amount: paymentRequest.amount,
					paid: true,
					shopperIds: [...new Set(productsForPayment.map((p) => p.shopperId))],
					productIds: productsForPayment.map((p) => p.id),
				};
				transactionApi.orders
					.doc(rentalInfo.id)
					.orderPaymentRequests.doc(orderPaymentRequest.id)
					.set(orderPaymentRequest);
				return receipt;
			});
			return receipt;
		} catch (err) {
			throw new Error(`Failed transaction: ${err.message}`);
		}
	};

	const renderTransactionView = () => {
		const numPadCellContent = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '00'];
		const defaultPaymentMethodOptions = getDefaultPaymentOptions();
		const customPaymentMethodOptions = shop.shopInfo.customPaymentMethods || [];
		return (
			<React.Fragment>
				<div className={classes.payContainer}>
					<Grid container className={classes.numPad} spacing={1}>
						<Grid item xs={12}>
							<div className={classNames(classes.standardCell, classes.amountCellWrapper)}>
								<Typography variant="h5" color={inputAmount > leftToPay ? 'error' : 'textPrimary'}>
									{getPricingString(inputAmount, currency)}
								</Typography>
								{inputAmount !== leftToPay && (
									<IconButton color="primary" size="small" onClick={() => resetAmountTo(leftToPay)}>
										<RiUploadLine />
									</IconButton>
								)}
							</div>
						</Grid>
						{numPadCellContent.map((content) => (
							<Grid item xs={4} key={content}>
								<Button
									fullWidth
									variant="outlined"
									className={classNames(classes.standardCell, classes.numPadCellWrapper)}
									onClick={() => setNewInputAmount(content)}
								>
									<Typography variant="h6">{content}</Typography>
								</Button>
							</Grid>
						))}
						<Grid item xs={4}>
							<Button
								fullWidth
								variant="outlined"
								className={classNames(
									classes.standardCell,
									classes.numPadCellWrapper,
									classes.delCell,
								)}
								onClick={() => resetAmountTo(0)}
							>
								<Typography variant="h6" color="error">
									{'DEL'}
								</Typography>
							</Button>
						</Grid>
					</Grid>
					<Grid container className={classes.methodCol} spacing={1}>
						{defaultPaymentMethodOptions.map((method) => (
							<Grid item xs={12} key={method}>
								<Button
									className={classNames(classes.standardCell, classes.methodCellWrapper)}
									fullWidth
									variant="contained"
									color="primary"
									onClick={() => payAmount(method)}
								>
									<Typography variant="body1">
										{LocaleHelpers.admin(t).paymentMethods[method] ?? method}
									</Typography>
								</Button>
							</Grid>
						))}
						{customPaymentMethodOptions.length ? (
							<Grid item xs={12}>
								{customPaymentMethodOptions.length === 1 ? (
									<Button
										className={classNames(classes.standardCell, classes.methodCellWrapper)}
										fullWidth
										variant="contained"
										color="primary"
										onClick={() => payAmount(customPaymentMethodOptions[0])}
									>
										<Typography variant="body1">{customPaymentMethodOptions[0]}</Typography>
									</Button>
								) : (
									<Button
										className={classNames(
											classes.standardCell,
											classes.methodCellWrapper,
											classes.moreCellWrapper,
										)}
										fullWidth
										variant="contained"
										color="secondary"
										onClick={() => setCustomPaymentMethodsDialogOpen(true)}
									>
										<Typography variant="body1">
											{t('common:more', 'More')}
											{'...'}
										</Typography>
									</Button>
								)}
							</Grid>
						) : null}
					</Grid>
				</div>
				{customPaymentMethodOptions.length > 1 && customPaymentMethodsDialogOpen && (
					<CustomPaymentMethodsDialog
						open={customPaymentMethodsDialogOpen}
						paymentMethods={customPaymentMethodOptions}
						handleClose={() => setCustomPaymentMethodsDialogOpen(false)}
						handlePaymentMethodSelection={(method: PaymentMethod) => {
							setCustomPaymentMethodsDialogOpen(false);
							payAmount(method);
						}}
					/>
				)}
			</React.Fragment>
		);
	};

	const renderCompletedView = () => {
		return (
			<div className={classes.completedView}>
				<Typography variant="h5" gutterBottom>
					{t('common:payment.paymentDone', 'Payment done')}
				</Typography>
				{changeToPay > 0 && (
					<Typography variant="h6" gutterBottom>
						{t('common:payment.toBePaidBack', 'To be paid back')}{' '}
						{getPricingString(changeToPay, currency)}
					</Typography>
				)}
				<div className={classes.btnSpacing}>
					<Button variant="contained" color="primary" onClick={handleClose}>
						{'Done'}
					</Button>
				</div>
			</div>
		);
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			scroll="paper"
			aria-labelledby="scroll-dialog-title"
			fullWidth
			fullScreen
			maxWidth={false}
		>
			<AppBar
				color="default"
				sx={{ backgroundColor: (theme) => theme.palette.background.paper }}
				position="relative"
				elevation={1}
			>
				<Toolbar>
					<IconButton color="inherit" onClick={handleClose} aria-label="Close" size="large">
						<CloseIcon />
					</IconButton>
					<Typography variant="h5" color="inherit" style={{ flexGrow: 1 }}>
						{'Pay'}
					</Typography>
				</Toolbar>
			</AppBar>
			<DialogContent className={classes.root}>
				<Container className={classes.mainContainer}>
					<div>
						<Typography variant="h5" className={classes.spacingBottom}>
							{t('common:payment.leftToPay', 'Left to pay')}
							{': '}
							{getPricingString(leftToPay, currency)}
						</Typography>
						{paymentLoading ? (
							<Spinner />
						) : changeToPay > 0 ? (
							renderCompletedView()
						) : leftToPay > 0 ? (
							renderTransactionView()
						) : null}
						{paymentError && <Typography>{paymentError}</Typography>}
					</div>
				</Container>
				<Paper square elevation={2} className={classes.sideBar}>
					<div className={classes.paymentRowContainer}>
						<PaymentRow
							description={`${t('common:payment.total')}`}
							value={getPricingString(amountToPay, currency)}
						/>
					</div>
					<Typography variant="subtitle1">
						{t('common:payment.paymentHistory', 'Payment history')}
					</Typography>
					{transactionsInfo.map((tRequest, i) => (
						<PaymentRow
							key={tRequest.transactionId}
							description={
								LocaleHelpers.admin(t).paymentMethods[tRequest.method] ?? tRequest.method
							}
							value={getPricingString(tRequest.amount.value, currency)}
							onRemoveClick={() => setTransactionsInfo(transactionsInfo.filter((_, j) => j !== i))}
							disabled={paymentLoading || !!receipt}
						/>
					))}
				</Paper>
			</DialogContent>
			{receipt && (
				<>
					<ReactToPrint
						trigger={() => (
							<button id="print-pay-receipt-button" style={{ display: 'none' }}>
								{'Print'}
							</button>
						)}
						content={() => receiptRef.current!}
						onAfterPrint={handlePrintingDone}
					/>
					<div style={{ display: 'none' }}>
						<PaymentReceipt receipt={receipt} ref={receiptRef as any} />
					</div>
				</>
			)}
		</Dialog>
	);
};

const styles = (theme: Theme) =>
	createStyles({
		root: {
			display: 'flex',
			height: '100%',
			padding: 0,
		},
		mainContainer: {
			flex: '1 1 auto',
			background: theme.palette.background.secondary,
			paddingTop: 32,
			overflow: 'auto',
			display: 'flex',
			justifyContent: 'center',
			position: 'relative',
		},
		payContainer: {
			display: 'flex',
			alignItems: ' flex-end',
		},
		numPad: {
			width: 270,
			position: 'relative',
		},
		standardCell: {
			border: `1px solid ${theme.palette.primary.main}`,
			borderRadius: 5,
			position: 'relative',
			cursor: 'pointer',
			padding: 0,
			minWidth: 0,
			minHeight: 0,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
		},
		numPadCellWrapper: {
			width: `calc(90px - ${theme.spacing(1)})`,
			height: `calc(80px - ${theme.spacing(1)})`,
		},
		amountCellWrapper: {
			width: '100%',
			cursor: 'auto',
			position: 'relative',
			justifyContent: 'space-between',
			padding: theme.spacing(2),
			marginBottom: theme.spacing(1),
		},
		methodCol: {
			width: 200,
			marginLeft: theme.spacing(2),
		},
		moreCellWrapper: {
			border: 'none',
		},
		methodCellWrapper: {
			width: '100%',
			height: `calc(80px - ${theme.spacing(1)})`,
		},
		delCell: {
			borderColor: theme.palette.error.main,
		},
		spacingBottomSmall: {
			marginBottom: theme.spacing(1),
		},
		spacingBottom: {
			marginBottom: theme.spacing(2),
		},
		paymentRowContainer: {
			padding: '16px 0',
		},
		shopperCard: {
			margin: theme.spacing(2, 0),
			padding: theme.spacing(1),
		},
		sideBar: {
			flex: '1 0 25%',
			maxWidth: 300,
			minWidth: 250,
			backgroundColor: theme.palette.background.paper,
			zIndex: 2,
			padding: '8px 16px',
			position: 'relative',
			display: 'flex',
			flexDirection: 'column',
			overflow: 'auto',
			justifyContent: 'flex-start',
		},
		productItem: {
			justifyContent: 'space-between',
			alignItems: 'flex-start',
			padding: '2px 0',
		},
		priceWrapper: {
			flex: '0 0 auto',
			textAlign: 'right',
		},
		divider: {
			margin: '4px 0',
		},
		paymentSummary: {
			padding: theme.spacing(0, 1),
		},
		btnSpacing: {
			margin: theme.spacing(2),
		},
		completedView: {
			textAlign: 'center',
			paddingTop: 32,
		},
		iconLeft: {
			marginRight: theme.spacing(1),
		},
	});

export default withStyles(styles)(PayDialog);
